<template>
  <div class="min-h-screen bg-gray-50">
    <AppHeader />
    
    <div class="max-w-2xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
      <div class="bg-white rounded-lg shadow-md p-8 text-center">
        <!-- Success Icon -->
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
          <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        
        <h1 class="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
        <p class="text-lg text-gray-600 mb-8">
          Thank you for your payment. Your order has been successfully processed.
        </p>
        
        <!-- Order Details -->
        <div v-if="order" class="bg-gray-50 rounded-lg p-6 mb-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div>
              <h3 class="font-medium text-gray-900 mb-2">Order Information</h3>
              <p class="text-sm text-gray-600">Order #{{ order.order_number }}</p>
              <p class="text-sm text-gray-600">{{ order.service_name }}</p>
              <p class="text-sm text-gray-600">Amount: ${{ order.total_amount?.toLocaleString() }}</p>
            </div>
            <div>
              <h3 class="font-medium text-gray-900 mb-2">Payment Details</h3>
              <p class="text-sm text-gray-600">Payment ID: {{ payment?.id || 'Processing...' }}</p>
              <p class="text-sm text-gray-600">Method: {{ payment?.payment_gateway || 'Card' }}</p>
              <p class="text-sm text-gray-600">Date: {{ formatDate(new Date()) }}</p>
            </div>
          </div>
        </div>
        
        <!-- Next Steps -->
        <div class="text-left mb-8">
          <h3 class="font-medium text-gray-900 mb-4">What happens next?</h3>
          <div class="space-y-3">
            <div class="flex items-start">
              <div class="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span class="text-xs font-medium text-blue-600">1</span>
              </div>
              <p class="text-sm text-gray-600">You will receive an email confirmation with your receipt and order details.</p>
            </div>
            <div class="flex items-start">
              <div class="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span class="text-xs font-medium text-blue-600">2</span>
              </div>
              <p class="text-sm text-gray-600">Our team will begin working on your project and contact you within 24 hours.</p>
            </div>
            <div class="flex items-start">
              <div class="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                <span class="text-xs font-medium text-blue-600">3</span>
              </div>
              <p class="text-sm text-gray-600">You can track your order progress in your dashboard at any time.</p>
            </div>
          </div>
        </div>
        
        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link to="/dashboard/client/orders" class="btn btn-primary">
            View My Orders
          </router-link>
          <router-link to="/dashboard/client" class="btn btn-outline">
            Go to Dashboard
          </router-link>
          <button @click="downloadInvoice" class="btn btn-outline">
            Download Invoice
          </button>
        </div>
        
        <!-- Support Info -->
        <div class="mt-8 pt-8 border-t border-gray-200">
          <p class="text-sm text-gray-600">
            Need help? Contact our support team at 
            <a href="mailto:support@ruxzom.com" class="text-primary-600 hover:text-primary-500">
              support@ruxzom.com
            </a>
            or call us at 
            <a href="tel:+1-555-123-4567" class="text-primary-600 hover:text-primary-500">
              +1 (555) 123-4567
            </a>
          </p>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orders'
import { usePaymentStore } from '@/stores/payments'
import { useUIStore } from '@/stores/ui'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const route = useRoute()
const router = useRouter()
const orderStore = useOrderStore()
const paymentStore = usePaymentStore()
const uiStore = useUIStore()

// State
const order = ref(null)
const payment = ref(null)

// Methods
const formatDate = (date) => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const downloadInvoice = async () => {
  try {
    if (order.value) {
      // This would typically generate and download a PDF invoice
      uiStore.showNotification({
        type: 'info',
        title: 'Invoice Download',
        message: 'Invoice download will be available soon. You will receive it via email.'
      })
    }
  } catch (error) {
    uiStore.showNotification({
      type: 'error',
      title: 'Download Failed',
      message: 'Failed to download invoice. Please try again later.'
    })
  }
}

// Initialize
onMounted(async () => {
  const orderId = route.params.id
  if (orderId) {
    try {
      order.value = await orderStore.fetchOrder(orderId)
      
      // Try to get the latest payment for this order
      const payments = await paymentStore.fetchPayments()
      payment.value = payments.find(p => p.order_id === parseInt(orderId))
      
      // Update order status to processing if payment was successful
      if (order.value && order.value.status !== 'processing') {
        await orderStore.updateOrderStatus(orderId, 'processing')
      }
    } catch (error) {
      console.error('Error loading order:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load order details.'
      })
      router.push('/orders')
    }
  } else {
    router.push('/orders')
  }
})
</script>
